// Unified market data adapter
// Routes to Binance (crypto) or synth (forex/commodities/indices) based on provider

import {
  fetchKlines as binKlines, fetch24hr as bin24hr, fetchDepth as binDepth,
  subscribeKline as binSubKline, subscribeTicker as binSubTicker,
  subscribeDepth as binSubDepth, subscribeTrades as binSubTrades,
  subscribeMultiTicker as binSubMulti, fmtPrice, SYMBOLS as CRYPTO_SYMBOLS, TIMEFRAMES
} from './binance';

import {
  synthFetchKlines, synthFetch24hr, synthFetchDepth,
  synthSubscribeKline, synthSubscribeTicker, synthSubscribeDepth,
  synthSubscribeTrades, synthSubscribeMulti, SYNTH_ASSETS
} from './synthMarket';

import {
  stocksFetchKlines, stocksFetch24hr, stocksFetchDepth,
  stocksSubscribeKline, stocksSubscribeTicker, stocksSubscribeDepth,
  stocksSubscribeTrades, stocksSubscribeMulti, STOCK_ASSETS
} from './stocksMarket';

import { amplify } from './volatility';

// Amplify the live `last` price on ticker streams (applies to every provider).
function wrapTicker(onMsg) {
  return (t) => {
    if (t && t.symbol && Number.isFinite(t.last)) onMsg({ ...t, last: amplify(t.symbol, t.last) });
    else onMsg(t);
  };
}

// Attach provider='binance' to crypto symbols + class='crypto'
const CRYPTO = CRYPTO_SYMBOLS.map(s => ({ ...s, provider: 'binance', class: 'crypto' }));

export const ALL_SYMBOLS = [...CRYPTO, ...STOCK_ASSETS, ...SYNTH_ASSETS];

export const ASSET_CLASSES = [
  { key: 'crypto',  label: 'Crypto',      color: '#f59e0b' },
  { key: 'stocks',  label: 'Stocks',      color: '#a855f7' },
  { key: 'forex',   label: 'Forex',       color: '#00e676' },
  { key: 'metals',  label: 'Metals',      color: '#eab308' },
  { key: 'energy',  label: 'Energy',      color: '#ef4444' },
  { key: 'index',   label: 'Indices',     color: '#0ea5e9' }
];

export { TIMEFRAMES, fmtPrice };

export async function fetchKlines(symbol, interval, limit = 300, market = 'spot', provider = 'binance') {
  if (provider === 'synth') return synthFetchKlines(symbol, interval, limit);
  if (provider === 'stocks') return stocksFetchKlines(symbol, interval, limit);
  return binKlines(symbol, interval, limit, market);
}

export async function fetch24hr(symbol, market = 'spot', provider = 'binance') {
  if (provider === 'synth') return synthFetch24hr(symbol);
  if (provider === 'stocks') return stocksFetch24hr(symbol);
  return bin24hr(symbol, market);
}

export async function fetchDepth(symbol, limit = 20, market = 'spot', provider = 'binance') {
  if (provider === 'synth') return synthFetchDepth(symbol);
  if (provider === 'stocks') return stocksFetchDepth(symbol);
  return binDepth(symbol, limit, market);
}

export function subscribeKline(symbol, interval, onMsg, market = 'spot', provider = 'binance') {
  if (provider === 'synth') return synthSubscribeKline(symbol, interval, onMsg);
  if (provider === 'stocks') return stocksSubscribeKline(symbol, interval, onMsg);
  return binSubKline(symbol, interval, onMsg, market);
}

export function subscribeTicker(symbol, onMsg, market = 'spot', provider = 'binance') {
  const cb = wrapTicker(onMsg);
  if (provider === 'synth') return synthSubscribeTicker(symbol, cb);
  if (provider === 'stocks') return stocksSubscribeTicker(symbol, cb);
  return binSubTicker(symbol, cb, market);
}

export function subscribeDepth(symbol, onMsg, market = 'spot', provider = 'binance') {
  if (provider === 'synth') return synthSubscribeDepth(symbol, onMsg);
  if (provider === 'stocks') return stocksSubscribeDepth(symbol, onMsg);
  return binSubDepth(symbol, onMsg, market);
}

export function subscribeTrades(symbol, onMsg, market = 'spot', provider = 'binance') {
  if (provider === 'synth') return synthSubscribeTrades(symbol, onMsg);
  if (provider === 'stocks') return stocksSubscribeTrades(symbol, onMsg);
  return binSubTrades(symbol, onMsg, market);
}

// Multi-ticker: split by provider
export function subscribeMultiTicker(symbolsWithProvider, onMsg, market = 'spot') {
  const cb = wrapTicker(onMsg);
  const cryptoSyms = symbolsWithProvider.filter(s => s.provider === 'binance').map(s => s.symbol);
  const synthSyms  = symbolsWithProvider.filter(s => s.provider === 'synth').map(s => s.symbol);
  const stockSyms  = symbolsWithProvider.filter(s => s.provider === 'stocks').map(s => s.symbol);
  const closers = [];
  if (cryptoSyms.length) closers.push(binSubMulti(cryptoSyms, cb, market));
  if (synthSyms.length) closers.push(synthSubscribeMulti(synthSyms, cb));
  if (stockSyms.length) closers.push(stocksSubscribeMulti(stockSyms, cb));
  return { close: () => closers.forEach(c => { try { c.close(); } catch {} }) };
}
